import React, { useLayoutEffect, useState } from 'react';
import {
  LocalizationContextProvider,
  MbmProvider,
  useLocalizationContext,
} from '@amzn/react-arb-tools';
import { initialLocalizationContext, mbm } from 'src/i18n';
import { isRTL } from 'src/i18n/locales';
import CloudscapeI18nProvider from 'src/i18n/CloudscapeI18nProvider';
import DefaultBundleProvider from 'src/i18n/shim/DefaultBundleProvider';

/**
 * Keeps the document's `lang` and `dir` attributes in step with the active locale. Both live on
 * `<html>` because Cloudscape and the `postcss-rtl` build step key off the document direction, and
 * because scrollbars and the native language `<select>` are painted outside the React tree.
 */
const DocumentLocaleAttributes: React.FC = () => {
  const { localizationContext } = useLocalizationContext();
  const locale = localizationContext.getLocale();

  useLayoutEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRTL(locale) ? 'rtl' : 'ltr';
  }, [locale]);

  return null;
};

interface Props {
  readonly children?: React.ReactNode;
}

/**
 * Makes localized strings available to the tree below it. Composition order matters:
 * `LocalizationContextProvider` is outermost because it owns the mutable locale; `MbmProvider`
 * receives a prebuilt `mbm` so one resource-pack cache is shared for the session;
 * `DefaultBundleProvider` resolves the bundle so `useTranslation` can be synchronous.
 *
 * `CloudscapeI18nProvider` sits inside `DefaultBundleProvider` so a bundle failure short-circuits
 * before Cloudscape's packs are fetched, and innermost so every Cloudscape component in `children`
 * is a descendant.
 */
const ArbProvider: React.FC<Props> = ({ children }) => {
  // Resolved once. Recomputing per render would re-read persisted storage every render and, after a
  // locale change, overwrite the context the picker just set.
  const [startingContext] = useState(initialLocalizationContext);

  return (
    <LocalizationContextProvider localizationContext={startingContext}>
      <MbmProvider mbm={mbm}>
        <DocumentLocaleAttributes />
        <DefaultBundleProvider>
          <CloudscapeI18nProvider>{children}</CloudscapeI18nProvider>
        </DefaultBundleProvider>
      </MbmProvider>
    </LocalizationContextProvider>
  );
};

export default ArbProvider;
